import { FALLBACK_IMAGES } from "../../media/cards/fallback-images"
import DurationMins from "./duration-mins"

function getMaskFromSeed(seed: string, index: number = 0): string {
  const hash = Array.from(seed).reduce((acc, char) => {
    const h = (acc << 5) - acc + char.charCodeAt(0)
    return h & h
  }, 0)
  return String(((Math.abs(hash) + index) % 9) + 1)
}

type CardProps = {
  heading?: string
  description?: string
  path: string
  mask?: boolean
  index?: number
  label?: string
  centerAlign?: boolean
  ctaLabel?: string
  hideCta?: boolean
  urlTarget?: string
  theme?: string
  durationMins?: string
  resourceType?: string
  logo?: {
    url: string
    title?: string
  }
  image?: {
    url: string
    alt?: string
    width?: number
    height?: number
  }
}

export default function Card({
  heading,
  description,
  path,
  mask,
  index = 0,
  label,
  centerAlign,
  ctaLabel,
  hideCta,
  urlTarget,
  theme,
  durationMins,
  resourceType,
  logo,
  image,
}: CardProps) {
  const cardMask = mask && getMaskFromSeed(path, index)
  const cardImage = (() => {
    if (resourceType === "webinar" && !image) {
      const themeSlug = theme?.toLowerCase()
      const imagePath =
        (themeSlug && FALLBACK_IMAGES[themeSlug]) || FALLBACK_IMAGES["general"]
      return {
        url: imagePath,
        alt: "Culture Amp on demand webinar",
        width: 800,
        height: 400,
      }
    }
    return image
  })()

  return (
    <article className="grid grid-rows-[auto_1fr] gap-y-16 h-full transition-all shadow-0 hover:shadow-1 hover:-translate-y-6 bg-white relative">
      {/* Image */}
      {cardImage && (
        <div className="px-24 pt-24" data-test="card-cardImage">
          <div className="aspect-w-2 aspect-h-1">
            <img
              className={`h-full w-full object-cover${cardMask ? ` mask mask--${cardMask}` : ""}`}
              data-ca-lazy-load={true}
              data-test={cardMask ? "has-mask" : "no-mask"}
              data-src={cardImage.url}
              alt={cardImage.alt || ""}
              width={cardImage.width}
              height={cardImage.height}
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className={`grid grid-flow-row gap-y-24 grid-rows-[auto,1fr] auto-rows-auto justify-items-start px-24 pb-24${!cardImage ? " pt-24 row-span-2" : ""}`}
      >
        {/* Top row */}
        <div
          className={`flex flex-col gap-y-16 w-full${centerAlign ? " items-center" : ""}`}
        >
          {label && <p className="eyebrow !text-black-70">{label}</p>}
          {logo && (
            <div>
              <img className="logo-sm" src={logo.url} alt={logo.title} />
            </div>
          )}
          {heading && (
            <h3
              className={`font-heading font-medium text-16 desktop:text-20 desktop:leading-[26px]${centerAlign ? " text-center" : ""}`}
              data-test="card-heading"
            >
              {heading}
            </h3>
          )}
        </div>

        {description && (
          <p
            className={`opacity-90 w-full${centerAlign ? " text-center" : ""}`}
            data-test="card-description"
          >
            {description}
          </p>
        )}

        {/* Bottom row */}
        {path && ctaLabel && (
          <a
            className={`${
              hideCta
                ? "absolute inset-0"
                : "button button--secondary before:absolute before:inset-0 self-end"
            }${!hideCta && centerAlign ? " mx-auto" : ""}`}
            href={path}
            target={urlTarget}
          >
            <span className={hideCta ? "sr-only" : ""}>{ctaLabel}</span>
          </a>
        )}
        {(theme || durationMins) && (
          <div className="flex justify-between items-end w-full">
            {theme && (
              <span className="inline-block eyebrow pr-20">{theme}</span>
            )}
            <div className="flex justify-end whitespace-nowrap">
              {durationMins && (
                <DurationMins length={durationMins} type={resourceType} />
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  )
}
